/**
 * Shared API/transport types.
 *
 * The backend (FastAPI) speaks snake_case; these types mirror that on the wire.
 */

/**
 * Normalized error thrown by the API client (`lib/api`).
 * Distinguishes HTTP errors (status > 0), auth/session errors (401),
 * and network failures (status 0).
 */
export interface ApiError {
  status: number;
  message: string;
  isWaitlist?: boolean;
  isNetworkError?: boolean;
  originalError?: unknown;
}

/** Type guard for {@link ApiError}-shaped values caught in `catch` blocks. */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    'message' in value
  );
}

/** Options accepted by the authenticated fetch helpers (superset of `RequestInit`). */
export type FetchOptions = RequestInit;

/** Standard list envelope returned by collection endpoints. */
export interface ListEnvelope<T> {
  items: T[];
  next_token: string | null;
}

/** Paginated list envelope returned by endpoints that also expose a total. */
export interface Paginated<T> extends ListEnvelope<T> {
  total: number;
}

/** `{ data, error }` result envelope used by token refresh + a few helpers. */
export interface ApiResult<T> {
  data: T | null;
  error: Error | null;
}

/** Auth tokens persisted to storage after sign-in. */
export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number | string;
}
